import {
  Component,
  input,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  viewChild,
  signal,
  afterNextRender,
  effect,
  HostListener,
} from '@angular/core';
import { Listing } from '../../dto';

@Component({
  selector: 'app-property-info',
  imports: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: {
    class: 'flex h-full',
  },
  templateUrl: './property-info.html',
})
export class PropertyInfo {
  private readonly MAX_FEATURES = 20; // max features to show
  public readonly listing = input.required<Listing>();
  private readonly featuresScroller = viewChild<ElementRef<HTMLDivElement>>('featuresScroller');
  public readonly showLeftFade = signal(false);
  public readonly showRightFade = signal(false);
  public readonly descriptionClampLines = signal(3);

  public readonly realtorInfo = computed(() => {
    const url = this.listing().source_url ?? '';
    if (url.includes('bee-realestate')) return { logo: '/bee-logo.svg', darkBg: false };
    if (url.includes('goldenhome')) return { logo: '/goldenhome-logo.png', darkBg: true };
    if (url.includes('altamira')) return { logo: '/altamira-logo.svg', darkBg: true };
    if (url.includes('remax-choice')) return { logo: '/remax-choice-logo.webp', darkBg: true };
    return null;
  });

  private readonly capitalizedFeatures = computed(() => {
    return (this.listing().features ?? []).map((feature) => {
      return feature.charAt(0).toUpperCase() + feature.slice(1);
    });
  });

  public readonly subtitleDetails = computed(() => {
    const listing = this.listing();
    const subtitle: string[] = [];
    if (listing.floor) {
      subtitle.push(listing.floor);
    }
    if (listing.bedrooms && listing.bedrooms !== '0') {
      subtitle.push(`${listing.bedrooms} δωμάτι${listing.bedrooms === '1' ? 'ο' : 'α'}`);
    }
    if (listing.year_built) {
      subtitle.push(`Έτος κατασκευής ${listing.year_built}`);
    }

    return subtitle.join(', ');
  });

  // Show max 4 features
  public readonly visibleFeatures = computed(() => {
    const features = this.capitalizedFeatures();
    return features.slice(0, this.MAX_FEATURES);
  });

  public isBeginning = true;
  public isEnd = false;

  private isDragging = false;
  private dragStartX = 0;
  private dragStartScrollLeft = 0;

  constructor() {
    effect(() => {
      this.visibleFeatures();
      queueMicrotask(() => {
        this.updateFeatureScrollState();
      });
    });

    afterNextRender(() => {
      this.updateFeatureScrollState();
    });
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.updateFeatureScrollState();
  }

  public onFeaturesPointerDown(event: PointerEvent): void {
    const container = event.currentTarget as HTMLElement | null;
    if (!container) {
      return;
    }

    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = container.scrollLeft;
    container.setPointerCapture(event.pointerId);
  }

  public onFeaturesPointerMove(event: PointerEvent): void {
    if (!this.isDragging) {
      return;
    }

    const container = event.currentTarget as HTMLElement | null;
    if (!container) {
      return;
    }

    const deltaX = event.clientX - this.dragStartX;
    container.scrollLeft = this.dragStartScrollLeft - deltaX;
    this.updateFeatureScrollState();
    event.preventDefault();
  }

  public onFeaturesPointerUp(event: PointerEvent): void {
    const container = event.currentTarget as HTMLElement | null;
    if (container?.hasPointerCapture(event.pointerId)) {
      container.releasePointerCapture(event.pointerId);
    }
    this.isDragging = false;
  }

  public updateFeatureScrollState(): void {
    const scroller = this.featuresScroller()?.nativeElement;
    if (!scroller) {
      this.showLeftFade.set(false);
      this.showRightFade.set(false);
      return;
    }

    const epsilon = 1;
    const canScroll = scroller.scrollWidth - scroller.clientWidth > epsilon;

    if (!canScroll) {
      this.showLeftFade.set(false);
      this.showRightFade.set(false);
      return;
    }

    const leftVisible = scroller.scrollLeft <= epsilon;
    const rightVisible =
      scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - epsilon;

    this.showLeftFade.set(!leftVisible);
    this.showRightFade.set(!rightVisible);
  }
}
